import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/shared/components/ui/sheet';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { Input } from '@/shared/components/ui/input';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Calendar } from '@/shared/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { CalendarIcon, Filter } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';

// Opções predefinidas de período
const DATE_RANGE_PRESETS = [
  { value: '7d', labelKey: 'dashboards.dateRange.last7Days' },
  { value: '14d', labelKey: 'dashboards.dateRange.last14Days' },
  { value: '30d', labelKey: 'dashboards.dateRange.last30Days' },
  { value: '60d', labelKey: 'dashboards.dateRange.last60Days' },
  { value: '90d', labelKey: 'dashboards.dateRange.last90Days' },
  { value: '6m', labelKey: 'dashboards.dateRange.last6Months' },
  { value: '1y', labelKey: 'dashboards.dateRange.lastYear' },
  { value: 'custom', labelKey: 'dashboards.dateRange.custom' },
];
import { Dashboard, FilterConfig, FilterValue } from '../types/Dashboard';
import { UpdateDashboardRequest } from '../types/DashboardRequests';
import { cn } from '@/infrastructure/lib/utils';
import { getLocalizedText } from '@/shared/utils/i18nHelper';

interface DashboardSettingsProps {
  dashboard: Dashboard;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (request: UpdateDashboardRequest) => void;
}

export const DashboardSettings = ({
  dashboard,
  open,
  onOpenChange,
  onSave,
}: DashboardSettingsProps) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language?.split('-')[0] || 'pt';
  
  // Inicializar filtros de forma reativa - prioriza filtros salvos do usuário
  const initialFilters = useMemo(() => {
    // Se houver filtros salvos pelo usuário, usar eles
    if (dashboard.filters && dashboard.filters.length > 0) {
      return dashboard.filters;
    }

    // Senão, usar defaultValue dos filtros padrão
    if (dashboard.default_filters && dashboard.default_filters.length > 0) {
      return dashboard.default_filters
        .filter(f => f.defaultValue !== undefined || f.default !== undefined)
        .map(f => ({
          id: f.id,
          value: f.defaultValue ?? f.default,
        }));
    }

    return [];
  }, [dashboard.filters, dashboard.default_filters]);

  const [filters, setFilters] = useState<FilterValue[]>(initialFilters);

  // Atualizar estado quando dashboard ou modal abrir
  useEffect(() => {
    if (open) {
      setFilters(initialFilters);
    }
  }, [open, initialFilters]);

  const handleSave = () => {
    onSave({
      filters,
    });
    onOpenChange(false);
  };

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterChange = (filterId: string, value: any) => {
    setFilters((prev) => {
      const existing = prev.find((f) => f.id === filterId);
      if (existing) {
        return prev.map((f) => (f.id === filterId ? { ...f, value } : f));
      }
      return [...prev, { id: filterId, value }];
    });
  };

  const getFilterValue = (filterId: string, filter: FilterConfig) => {
    const savedValue = filters.find((f) => f.id === filterId)?.value;
    return savedValue ?? filter.defaultValue ?? filter.default;
  };

  // Normalizar options para formato {label, value} com suporte a i18n
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const normalizeOptions = (options?: any[]) => {
    if (!options) return [];

    return options.map(opt => {
      // Se for string simples
      if (typeof opt === 'string') {
        return { label: opt, value: opt };
      }

      // Se for objeto {label, value}
      if (typeof opt === 'object' && opt !== null) {
        return {
          label: getLocalizedText(opt.label, currentLanguage),
          value: opt.value ?? opt.label, // fallback para label se não tiver value
        };
      }

      return { label: String(opt), value: String(opt) };
    });
  };

  // Some fields come from the backend as JSON-serialized i18n maps (e.g.
  // dashboards.name = '{"en":"...","pt":"..."}'). parseIfJSON unwraps those
  // so getLocalizedText can pick the locale; non-JSON strings pass through.
  const parseIfJSON = (value: unknown): unknown => {
    if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  };

  const getDashboardName = (): string => {
    if (dashboard.customName) {
      return getLocalizedText(parseIfJSON(dashboard.customName), currentLanguage);
    }
    return getLocalizedText(parseIfJSON(dashboard.name), currentLanguage);
  };

  // Helper para obter textos traduzidos do filtro
  const getFilterLabel = (filter: FilterConfig): string => {
    return getLocalizedText(filter.label, currentLanguage) ||
           getLocalizedText(filter.name, currentLanguage) ||
           filter.id;
  };

  const getFilterPlaceholder = (filter: FilterConfig): string => {
    return getLocalizedText(filter.placeholder, currentLanguage) ||
           getLocalizedText(filter.name, currentLanguage) ||
           getLocalizedText(filter.label, currentLanguage) ||
           '';
  };

  const renderFilterInput = (filter: FilterConfig) => {
    const value = getFilterValue(filter.id, filter);
    const options = normalizeOptions(filter.options);

    switch (filter.type) {
      case 'select':
        return (
          <Select
            value={value}
            onValueChange={(val) => handleFilterChange(filter.id, val)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('common.select')} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'multiselect': {
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2 border rounded-md p-3 max-h-[200px] overflow-y-auto">
            {options.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`${filter.id}-${option.value}`}
                  checked={selectedValues.includes(option.value)}
                  onCheckedChange={(checked) => {
                    const newValues = checked
                      ? [...selectedValues, option.value]
                      : selectedValues.filter((v) => v !== option.value);
                    handleFilterChange(filter.id, newValues);
                  }}
                />
                <label
                  htmlFor={`${filter.id}-${option.value}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );
      }

      case 'date':
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !value && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {value ? format(new Date(value), 'PPP') : t('common.pickDate')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={value ? new Date(value) : undefined}
                onSelect={(date) => handleFilterChange(filter.id, date?.toISOString())}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleFilterChange(filter.id, Number(e.target.value))}
            placeholder={getFilterPlaceholder(filter)}
          />
        );

      case 'text':
        return (
          <Input
            type="text"
            value={value || ''}
            onChange={(e) => handleFilterChange(filter.id, e.target.value)}
            placeholder={getFilterPlaceholder(filter)}
          />
        );

      case 'daterange':
      case 'date-range': {
        // Valor pode ser string predefinida ("30d") ou objeto {preset, startDate, endDate}
        const rangeValue = typeof value === 'string'
          ? { preset: value }
          : (value || { preset: '30d' });

        const currentPreset = rangeValue.preset || '30d';
        const isCustom = currentPreset === 'custom';
        const language = navigator.language || 'pt-BR';
        const locale = language.startsWith('en') ? enUS : ptBR;

        return (
          <div className="space-y-3">
            <Select
              value={currentPreset}
              onValueChange={(preset) => {
                if (preset === 'custom') {
                  // Inicializar com últimos 30 dias quando mudar para custom
                  const endDate = new Date();
                  const startDate = subDays(endDate, 30);
                  handleFilterChange(filter.id, {
                    preset: 'custom',
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                  });
                } else {
                  handleFilterChange(filter.id, preset);
                }
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('dashboards.dateRange.selectPeriod')} />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGE_PRESETS.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {t(preset.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {isCustom && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">{t('dashboards.dateRange.startDate')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !rangeValue.startDate && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-3 w-3" />
                        {rangeValue.startDate
                          ? format(new Date(rangeValue.startDate), 'dd/MM/yyyy', { locale })
                          : t('dashboards.dateRange.selectDate')
                        }
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={rangeValue.startDate ? new Date(rangeValue.startDate) : undefined}
                        onSelect={(date) => {
                          handleFilterChange(filter.id, {
                            ...rangeValue,
                            startDate: date?.toISOString(),
                          });
                        }}
                        locale={locale}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">{t('dashboards.dateRange.endDate')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !rangeValue.endDate && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-3 w-3" />
                        {rangeValue.endDate
                          ? format(new Date(rangeValue.endDate), 'dd/MM/yyyy', { locale })
                          : t('dashboards.dateRange.selectDate')
                        }
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={rangeValue.endDate ? new Date(rangeValue.endDate) : undefined}
                        onSelect={(date) => {
                          handleFilterChange(filter.id, {
                            ...rangeValue,
                            endDate: date?.toISOString(),
                          });
                        }}
                        locale={locale}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[540px] flex flex-col">
        <SheetHeader>
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('dashboards.filters', 'Filtros')}
            </p>
            <SheetTitle className="text-2xl font-bold">
              {getDashboardName()}
            </SheetTitle>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="space-y-6 py-6 px-1">

            {dashboard.default_filters && dashboard.default_filters.length > 0 ? (
              <div className="space-y-4">
                <Label className="text-base font-semibold">{t('dashboards.filters')}</Label>
                {dashboard.default_filters.map((filter) => (
                  <div key={filter.id} className="space-y-2">
                    <Label className="text-sm font-normal">
                      {getFilterLabel(filter)}
                    </Label>
                    {renderFilterInput(filter)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                <Filter className="h-8 w-8 text-muted-foreground/60" />
                <p className="text-sm text-muted-foreground max-w-[320px]">
                  {t(
                    'dashboards.filters.empty',
                    'Este dashboard não tem filtros configuráveis.'
                  )}
                </p>
              </div>
            )}
          </div>
        </ScrollArea>

        {dashboard.default_filters && dashboard.default_filters.length > 0 && (
          <SheetFooter className="mt-auto pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave}>{t('common.save')}</Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
};
